import { BrainLogo } from '@/components/ui/brain-logo'
import { ArrowRight, Brain, Briefcase } from 'lucide-react'
import { SlideIn, FadeIn, StaggerContainer, StaggerItem, ScaleIn } from '@/components/motion'

interface RoleSelectionStepProps {
  onSelectRole: (role: 'genius' | 'client') => void
}

export function RoleSelectionStep({ onSelectRole }: RoleSelectionStepProps) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <ScaleIn className="w-full max-w-[900px]">
        <div className="bg-white rounded-[40px] border border-slate-200 overflow-hidden shadow-sm">
          {/* Header with gradient */}
          <div
            className="pt-16 px-8 pb-12 flex flex-col items-center"
            style={{
              background: 'linear-gradient(180deg, rgba(248, 249, 255, 1) 0%, rgba(255, 255, 255, 1) 100%)',
            }}
          >
            <FadeIn delay={0.1}>
              <BrainLogo size="lg" variant="light" showGlow={false} />
            </FadeIn>
            <SlideIn direction="up" delay={0.2}>
              <h1 className="text-4xl md:text-5xl font-black text-slate-900 text-center mt-6">
                Welcome! Let's Get Started
              </h1>
            </SlideIn>
            <SlideIn direction="up" delay={0.3}>
              <p className="text-xl text-slate-500 text-center mt-4">
                Choose your path to begin your journey
              </p>
            </SlideIn>
          </div>

          {/* Role Cards */}
          <StaggerContainer staggerDelay={0.15} delayChildren={0.4} className="px-8 md:px-16 pb-16 flex flex-col md:flex-row gap-8 justify-center">
            {/* Client Card - White with strokes */}
            <StaggerItem className="flex-1 max-w-[380px]">
              <button
                onClick={() => onSelectRole('client')}
                className="w-full h-full bg-white border-2 border-primary/30 rounded-[40px] p-8 pt-12 flex flex-col items-center text-center hover:shadow-lg hover:border-primary/50 transition-all hover:scale-[1.02] focus:outline-none focus:ring-4 focus:ring-primary/20"
              >
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center mb-6 border-2 border-primary/20 bg-primary/5"
                >
                  <Briefcase className="w-6 h-6 text-[#4669ed]" />
                </div>
                <h3 className="text-2xl md:text-[32px] font-extrabold text-slate-900 mb-4">
                  Hire An Expert
                </h3>
                <p className="text-lg text-slate-500 leading-relaxed mb-8 flex-grow">
                  Find vetted AI experts.
                </p>
                <div
                  className="mt-auto rounded-[48px] py-4 px-8 flex items-center gap-2"
                  style={{
                    background: 'linear-gradient(108.7deg, rgba(54, 84, 233, 1) 0%, rgba(70, 105, 237, 1) 100%)',
                  }}
                >
                  <span className="text-white font-bold">Continue as Client</span>
                  <ArrowRight className="w-6 h-6 text-white" />
                </div>
              </button>
            </StaggerItem>

            {/* Expert Card */}
            <StaggerItem className="flex-1 max-w-[380px]">
              <button
                onClick={() => onSelectRole('genius')}
                className="w-full h-full bg-[#e9f1fe] rounded-[40px] p-8 pt-12 flex flex-col items-center text-center hover:shadow-lg transition-all hover:scale-[1.02] focus:outline-none focus:ring-4 focus:ring-[#4669ed]/20"
              >
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center mb-6"
                  style={{
                    background: 'linear-gradient(145.3deg, rgba(54, 84, 233, 1) 0%, rgba(70, 105, 237, 1) 100%)',
                  }}
                >
                  <Brain className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl md:text-[32px] font-extrabold text-slate-900 mb-4">
                  Join as Expert
                </h3>
                <p className="text-lg text-slate-500 leading-relaxed mb-8 flex-grow">
                  Showcase your AI expertise and
                  <br />
                  get matched with top companies
                </p>
                <div
                  className="mt-auto rounded-[48px] py-4 px-8 flex items-center gap-2"
                  style={{
                    background: 'linear-gradient(108.31deg, rgba(54, 84, 233, 1) 0%, rgba(70, 105, 237, 1) 100%)',
                  }}
                >
                  <span className="text-white font-bold">Continue as Expert</span>
                  <ArrowRight className="w-6 h-6 text-white" />
                </div>
              </button>
            </StaggerItem>
          </StaggerContainer>
        </div>
      </ScaleIn>
    </div>
  )
}
